/**
 * Mission, launch site, orbit and manoeuvre schemas.
 */

import { z } from 'zod';
import {
  AuditFieldsSchema,
  BodyIdSchema,
  type BodyIdDto,
  CrewKindSchema,
  GeodeticSchema,
  IdSchema,
  type MissionType,
  MissionTypeSchema,
  OrbitalElementsSchema,
  TimestampSchema,
} from './primitives.js';

/**
 * Destination bodies supported by the end-to-end propagator for each mission
 * profile. Keeping this beside the mission contract gives the wizard, inspector,
 * browser worker, and API one shared compatibility rule instead of letting an
 * unsupported pair silently run a different trajectory.
 */
export const MISSION_DESTINATIONS = {
  suborbital: ['earth'],
  leo: ['earth'],
  meo: ['earth'],
  geo: ['earth'],
  lunar: ['moon'],
  interplanetary: ['venus', 'mars', 'jupiter'],
  escape: ['earth'],
} as const satisfies Record<MissionType, readonly BodyIdDto[]>;

export function isMissionDestinationSupported(
  missionType: MissionType,
  destinationBody: BodyIdDto,
): boolean {
  return (MISSION_DESTINATIONS[missionType] as readonly BodyIdDto[]).includes(
    destinationBody,
  );
}

export function missionDestinationError(
  missionType: MissionType,
  destinationBody: BodyIdDto,
): string | null {
  if (isMissionDestinationSupported(missionType, destinationBody)) return null;
  const supported = MISSION_DESTINATIONS[missionType].join(', ');
  return `${missionType} missions support ${supported} as the destination, not ${destinationBody}. Change the mission profile or destination before simulating.`;
}

export const LaunchSiteSchema = z.object({
  id: IdSchema,
  name: z.string().min(1).max(160),
  facility: z.string().max(160),
  country: z.string().max(120),
  agency: z.string().max(160),
  latitudeDeg: z.number().min(-90).max(90),
  longitudeDeg: z.number().min(-180).max(180),
  elevationMeters: z.number(),
  /** Permitted launch azimuth corridor, degrees clockwise from north. */
  azimuthRangeDeg: z.tuple([z.number(), z.number()]),
  notes: z.string().max(2000).optional(),
  crewRated: z.boolean().default(false),
});
export type LaunchSiteDto = z.infer<typeof LaunchSiteSchema>;

/**
 * Weather at the launch site.
 *
 * Modelled as a parametric profile rather than a live meteorological feed: the
 * simulation needs a wind field it can evaluate deterministically at any altitude,
 * and a deterministic field is also what makes a recorded flight replayable. See
 * `docs/09-mathematical-models.md`.
 */
export const WeatherSchema = z.object({
  /** Surface temperature, kelvin. */
  surfaceTemperatureK: z.number().min(180).max(340).default(288.15),
  /** Surface pressure, pascals. */
  surfacePressurePa: z.number().min(50_000).max(110_000).default(101_325),
  /** Surface wind speed, metres per second. */
  surfaceWindSpeed: z.number().min(0).max(60).default(4),
  /** Direction the surface wind blows towards, degrees clockwise from north. */
  surfaceWindDirectionDeg: z.number().min(0).max(360).default(90),
  /** Peak jet-stream wind speed, metres per second. */
  jetStreamSpeed: z.number().min(0).max(120).default(45),
  /** Altitude of the jet-stream peak, metres. */
  jetStreamAltitudeMeters: z.number().min(5000).max(20_000).default(11_000),
  /** Gust amplitude, metres per second. */
  gustAmplitude: z.number().min(0).max(30).default(3),
  /** Relative humidity, 0-1. Informational; affects launch constraints only. */
  relativeHumidity: z.number().min(0).max(1).default(0.6),
  /** Cloud ceiling, metres. Null for clear skies. */
  cloudCeilingMeters: z.number().min(0).max(20_000).nullable().default(null),
  /** Whether lightning has been detected within the launch constraint radius. */
  lightningWithinConstraintRadius: z.boolean().default(false),
  /** Thermosphere density multiplier, standing in for solar activity. */
  solarActivity: z.number().min(0.25).max(4).default(1),
});
export type WeatherDto = z.infer<typeof WeatherSchema>;

export const AscentProgramSchema = z.object({
  azimuthDeg: z.number().min(-180).max(360),
  pitchStartAltitudeMeters: z.number().min(0).max(20_000).default(500),
  turnEndAltitudeMeters: z.number().min(10_000).max(400_000).default(120_000),
  turnExponent: z.number().min(0.1).max(3).default(0.5),
  targetApoapsisAltitudeMeters: z.number().min(1000).max(500_000_000),
  targetPeriapsisAltitudeMeters: z.number().min(-500_000).max(500_000_000),
  closedLoopAltitudeMeters: z.number().min(10_000).max(200_000).default(55_000),
  verticalSpeedTimeConstantSeconds: z.number().min(10).max(1000).default(200),
  maxAngleOfAttackDeg: z.number().min(0).max(30).default(6),
  angleOfAttackQThresholdPa: z.number().min(0).max(100_000).default(2000),
  maxQThrottleThresholdPa: z.number().min(0).max(100_000).default(0),
  maxQThrottle: z.number().min(0.1).max(1).default(0.7),
});
export type AscentProgramDto = z.infer<typeof AscentProgramSchema>;

export const ManeuverKindSchema = z.enum([
  'prograde',
  'retrograde',
  'normal',
  'anti-normal',
  'radial-in',
  'radial-out',
  'hohmann-transfer',
  'bi-elliptic-transfer',
  'plane-change',
  'combined-plane-change',
  'phasing',
  'circularise',
  'deorbit',
  'escape',
  'lambert',
]);
export type ManeuverKind = z.infer<typeof ManeuverKindSchema>;

export const ManeuverSchema = z.object({
  id: IdSchema,
  name: z.string().max(160),
  kind: ManeuverKindSchema,
  /** Mission elapsed time at which the burn starts, seconds. */
  startTimeSeconds: z.number().min(0),
  /** Delta-V magnitude, metres per second. */
  deltaVMetersPerSecond: z.number().min(0),
  /** Delta-V components in the local orbital frame, metres per second. */
  deltaVLvlh: z
    .object({
      prograde: z.number(),
      normal: z.number(),
      radial: z.number(),
    })
    .optional(),
  /** Target orbit, for manoeuvres expressed as a destination. */
  targetElements: OrbitalElementsSchema.optional(),
  notes: z.string().max(2000).optional(),
});
export type ManeuverDto = z.infer<typeof ManeuverSchema>;

export const MissionSchema = z.object({
  id: IdSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(8000).default(''),
  missionType: MissionTypeSchema,
  /** Launch epoch. */
  launchAt: TimestampSchema,
  launchSiteId: IdSchema,
  /** Overridden site coordinates, for a site not in the catalogue. */
  launchSiteOverride: GeodeticSchema.optional(),
  destinationBody: BodyIdSchema.default('earth'),
  payloadMassKg: z.number().nonnegative().max(1e7),
  crewKind: CrewKindSchema.default('cargo'),
  crewCount: z.number().int().min(0).max(20).default(0),
  rocketId: IdSchema.nullable(),
  targetOrbit: OrbitalElementsSchema.optional(),
  ascentProgram: AscentProgramSchema.optional(),
  weather: WeatherSchema.optional(),
  maneuvers: z.array(ManeuverSchema).max(64).default([]),
  tags: z.array(z.string().max(40)).max(20).default([]),
});
export type MissionDto = z.infer<typeof MissionSchema>;

export const MissionRecordSchema = MissionSchema.extend(AuditFieldsSchema.shape).extend({
  projectId: IdSchema.nullable(),
  ownerId: IdSchema,
});
export type MissionRecord = z.infer<typeof MissionRecordSchema>;

export const CreateMissionSchema = MissionSchema.omit({ id: true }).extend({
  projectId: IdSchema.optional(),
});
export type CreateMission = z.infer<typeof CreateMissionSchema>;

export const UpdateMissionSchema = CreateMissionSchema.partial();
export type UpdateMission = z.infer<typeof UpdateMissionSchema>;

/** An immutable snapshot in a mission's version history. */
export const MissionVersionSchema = z.object({
  id: IdSchema,
  missionId: IdSchema,
  version: z.number().int().nonnegative(),
  /** Full mission document as it stood at this version. */
  snapshot: MissionSchema,
  /** What changed, when the client supplied a label. */
  label: z.string().max(200).nullable(),
  createdAt: TimestampSchema,
  createdBy: IdSchema.nullable(),
});
export type MissionVersionDto = z.infer<typeof MissionVersionSchema>;

export const ProjectSchema = z.object({
  id: IdSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(4000).default(''),
});
export type ProjectDto = z.infer<typeof ProjectSchema>;

export const ProjectRecordSchema = ProjectSchema.extend(AuditFieldsSchema.shape).extend({
  ownerId: IdSchema,
  missionCount: z.number().int().nonnegative().optional(),
});
export type ProjectRecord = z.infer<typeof ProjectRecordSchema>;
